import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  DollarSign,
  Cpu,
  Shield,
  TrendingUp,
  ClipboardList,
  Users,
  FileText,
  BookOpen,
  ScrollText,
  Map,
  Ticket,
  BarChart3,
  ThumbsUp,
  Headphones,
  Megaphone,
  Workflow,
  GraduationCap,
  Link2,
  Sparkles,
  Activity,
  Eye,
  Sparkle,
} from 'lucide-react'
import { useSession } from '@/context/SessionContext'
import { useContentContext } from '@/hooks/useContent'
import { SECTIONS } from '@/pages/admin/dashboard/ProjectPanel'
import { PAGE_LINKS } from '@/data/pageLinks'
import ErrorBoundary from '@/components/layout/ErrorBoundary'

// ── Sidebar nav structure ─────────────────────────────────────────
// Groups: each has a label, items, and optionally a collapsible state.
// Items in the top group (no label) are always visible as primary nav.
const NAV_GROUPS = [
  {
    label: null, // primary — always visible
    items: [{ to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true }],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/finance', icon: DollarSign, label: 'Finance & Revenue' },
      { to: '/admin/ai-ops', icon: Cpu, label: 'AI Operations' },
      { to: '/admin/automations', icon: Workflow, label: 'AI Automations' },
      { to: '/admin/system-health', icon: Shield, label: 'System Health' },
      { to: '/admin/support', icon: Headphones, label: 'Support Desk' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/admin/users', icon: Users, label: 'User Intelligence' },
      { to: '/admin/analytics', icon: TrendingUp, label: 'Advanced Analytics' },
      { to: '/admin/chat-analytics', icon: BarChart3, label: 'Chat Analytics' },
      { to: '/admin/feedback', icon: ThumbsUp, label: 'Response Feedback' },
      { to: '/admin/reports', icon: ClipboardList, label: 'Reports & Exports' },
    ],
  },
  {
    label: 'Content & Updates',
    items: [
      { to: '/admin/studio', icon: Sparkles, label: 'Content Studio', prefix: true },
      { to: '/admin/blog-pipeline', icon: FileText, label: 'Blog Pipeline', prefix: true },
      { to: '/admin/features', icon: GraduationCap, label: 'Features', prefix: true },
      { to: '/admin/patch-notes', icon: ScrollText, label: 'Patch Notes' },
      { to: '/admin/roadmap', icon: Map, label: 'Product Roadmap' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/links', icon: Link2, label: 'Content Links' },
      { to: '/admin/beta-invites', icon: Ticket, label: 'Invite Codes' },
    ],
  },
  {
    label: 'Previews',
    items: [
      { to: '/admin/onboarding-preview', icon: Sparkle, label: 'Onboarding Preview (V7)' },
      { to: '/admin/demos', icon: Eye, label: 'Demo Preview' },
    ],
  },
]

function NavItem({ item, pathname, onClick }) {
  const isActive = item.exact
    ? pathname === item.to
    : item.prefix
      ? pathname.startsWith(item.to)
      : pathname === item.to
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
        isActive
          ? 'bg-gold/20 text-gold font-medium'
          : 'text-warm-cream/60 hover:text-gold hover:bg-white/5'
      }`}
    >
      <span className="flex items-center gap-2.5">
        <Icon className="w-4 h-4" />
        {item.label}
      </span>
      <ChevronRight className="w-3 h-3 opacity-40" aria-hidden="true" />
    </Link>
  )
}

function NavGroup({ group, pathname, onClick }) {
  const [open, setOpen] = useState(true)
  // Auto-open if any child is active
  const isAnyActive = group.items.some(item =>
    item.exact
      ? pathname === item.to
      : item.prefix
        ? pathname.startsWith(item.to)
        : pathname === item.to
  )
  const isOpen = open || isAnyActive

  if (!group.label) {
    return group.items.map(item => (
      <NavItem key={item.to} item={item} pathname={pathname} onClick={onClick} />
    ))
  }

  return (
    <div className="mt-4 first:mt-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-3 py-1.5 mb-1"
      >
        <span className="text-warm-cream/30 text-xs font-semibold uppercase tracking-wider">
          {group.label}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-warm-cream/20 transition-transform ${isOpen ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
      </button>
      {isOpen &&
        group.items.map(item => (
          <NavItem key={item.to} item={item} pathname={pathname} onClick={onClick} />
        ))}
    </div>
  )
}

function CollapsibleSection({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-3 py-1.5 mb-1"
      >
        <span className="text-warm-cream/30 text-xs font-semibold uppercase tracking-wider">
          {label}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-warm-cream/20 transition-transform ${open ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
      </button>
      {open && children}
    </div>
  )
}

export default function AdminLayout() {
  const { user, signOut } = useSession()
  const { contentMap } = useContentContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen])

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  const closeSidebar = () => setSidebarOpen(false)

  return (
    <div className="admin-chrome min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed left-4 z-50 bg-midnight text-gold p-2 rounded-lg shadow-lg"
        style={{ top: 'calc(1rem + env(safe-area-inset-top))' }}
      >
        {sidebarOpen ? (
          <X className="w-5 h-5" aria-hidden="true" />
        ) : (
          <Menu className="w-5 h-5" aria-hidden="true" />
        )}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-midnight text-warm-cream transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 flex flex-col`}
      >
        <div className="p-6 border-b border-white/10">
          <Link to="/?from=app" className="flex items-center gap-2">
            <img
              src="/FinalLogo.png"
              alt="Illuminate Faith"
              className="h-8 w-auto rounded"
              style={{ clipPath: 'inset(2% round 4px)' }}
            />
            <span className="font-heading text-gold text-lg font-bold">Admin Panel</span>
          </Link>
          <p className="text-warm-cream/40 text-xs mt-2 truncate">{user?.email}</p>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          {/* Main navigation groups */}
          {NAV_GROUPS.map((group, i) => (
            <NavGroup
              key={group.label || i}
              group={group}
              pathname={location.pathname}
              onClick={closeSidebar}
            />
          ))}

          {/* Pages — collapsible */}
          <CollapsibleSection label="Pages" defaultOpen={false}>
            {PAGE_LINKS.map(page => (
              <a
                key={page.href}
                href={page.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeSidebar}
                className="flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-0.5 text-warm-cream/60 hover:text-gold hover:bg-white/5 transition-colors"
              >
                <span>{page.label}</span>
                <ExternalLink className="w-3 h-3 opacity-40" aria-hidden="true" />
              </a>
            ))}
          </CollapsibleSection>

          {/* Sections — collapsible */}
          <CollapsibleSection
            label="Sections"
            defaultOpen={location.pathname.startsWith('/admin/sections')}
          >
            {SECTIONS.map(section => {
              const row = contentMap.get(section.key)
              const isActive = location.pathname === `/admin/sections/${section.key}`
              const isDraft = row && !row.is_published
              return (
                <Link
                  key={section.key}
                  to={`/admin/sections/${section.key}`}
                  onClick={closeSidebar}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                    isActive
                      ? 'bg-gold/20 text-gold'
                      : 'text-warm-cream/60 hover:text-gold hover:bg-white/5'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {section.label}
                    {isDraft && (
                      <span
                        className="w-2 h-2 rounded-full bg-yellow-400"
                        title="Has unpublished changes"
                      />
                    )}
                  </span>
                  <ChevronRight className="w-3 h-3 opacity-40" aria-hidden="true" />
                </Link>
              )
            })}
          </CollapsibleSection>
        </nav>

        <div className="p-4 border-t border-white/10 space-y-1">
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 text-warm-cream/50 hover:text-red-400 text-sm transition-colors w-full"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content. Zone-level ErrorBoundary (FE-029) so an admin-page
          crash doesn't whitescreen the sidebar — admin can still navigate
          to other admin pages or sign out. */}
      <main id="main-content" className="flex-1 min-h-screen md:ml-0 overflow-x-hidden">
        <div
          className="px-4 pb-6 md:p-8 md:pt-8"
          style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  )
}
